import { echeancier as sch, formaterJour, formaterMontant } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, ErreurApi, lire, requetes } from '../api'
import { CopierLien } from '../composants/CopierLien'
import { Formulaire } from '../composants/Formulaire'
import { Historique } from '../composants/Historique'
import { Ligne, Section, Vide } from '../composants/Section'
import { LIBELLES_CERTITUDE } from '../libelles'
import { BadgeStatut, datesEcheance } from './Echeancier'
import { LigneTache } from '../composants/LigneTache'
import { LignePaiement } from './Paiements'
import { LignePiece } from './Pieces'
import { FormulaireTache } from './Taches'
import { confirmer } from '../composants/confirmation'

export function EcheanceFiche({ id }: { id: string }) {
  const client = useQueryClient()
  const fiche = useQuery(requetes.echeance(id))
  const lesPaiements = useQuery(requetes.paiements({ echeanceId: id }))
  const lesTaches = useQuery(requetes.taches({ echeanceId: id }))
  const piecesEcheance = useQuery(requetes.pieces({ echeanceId: id, vue: 'toutes' }))
  const entites = useQuery(requetes.entites)
  const [marquer, setMarquer] = useState(false)
  // Preuves proposées : pièces de l'échéance, puis pièces récentes de l'entité.
  const piecesEntite = useQuery({
    ...requetes.pieces({ entiteId: fiche.data?.entiteId ?? '', vue: 'recentes' }),
    enabled: marquer && Boolean(fiche.data),
  })
  const [erreur, setErreur] = useState<string | null>(null)
  const rafraichir = () => client.invalidateQueries()

  async function agir(action: () => Promise<unknown>) {
    setErreur(null)
    try {
      await action()
      await rafraichir()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }

  if (fiche.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (!fiche.data) {
    return (
      <main>
        <p className="alerte">Échéance introuvable.</p>
        <Link to="/echeancier">Retour à l’échéancier</Link>
      </main>
    )
  }
  const e = fiche.data
  const entite = entites.data?.find((x) => x.id === e.entiteId)
  const ecriture = entite ? !entite.archivee : false
  const ouverte = e.statut === 'a_venir' || e.statut === 'en_preparation'
  const param = { param: { id } }

  return (
    <main>
      <header className="entete-fiche">
        <p className="fil">
          <Link to="/echeancier">Échéancier</Link> ·{' '}
          <Link to="/entites/$id" params={{ id: e.entiteId }}>
            {e.entite}
          </Link>
        </p>
        <h1>
          {e.libelle} <span className="discret">{e.periode}</span>
        </h1>
        <p>
          <BadgeStatut statut={e.statut} />
          {e.certitude === 'a_confirmer' && <span className="badge">À confirmer</span>}
          {e.dateForcee && <span className="badge">Date saisie à la main</span>}{' '}
          <CopierLien objet="de l’échéance" />
        </p>
      </header>

      <Section titre="Échéance">
        <Ligne
          edition={
            ecriture && ouverte
              ? (fermer) => (
                  <Formulaire
                    champs={[
                      { nom: 'libelle', libelle: 'Libellé', type: 'texte', requis: true },
                      { nom: 'date', libelle: 'Date', type: 'date', requis: true },
                      { nom: 'finFenetre', libelle: 'Fin de fenêtre', type: 'date' },
                      { nom: 'montantPrevuCts', libelle: 'Montant prévu (€)', type: 'montant' },
                    ]}
                    initial={e}
                    onAnnuler={fermer}
                    onValider={async (v) => {
                      // Seule une date modifiée devient « saisie à la main ».
                      const { date, finFenetre, ...reste } = v
                      await lire(
                        api.echeances[':id'].$patch({
                          ...param,
                          json: corps(sch.echeanceModification, {
                            ...reste,
                            ...(date !== e.date && { date }),
                            ...(finFenetre !== e.finFenetre && { finFenetre }),
                          }),
                        }),
                      )
                      await rafraichir()
                      fermer()
                    }}
                  />
                )
              : undefined
          }
        >
          <dl className="details">
            <dt>Date</dt>
            <dd>{datesEcheance(e)}</dd>
            {e.dateTheorique !== e.date && (
              <>
                <dt>Date théorique</dt>
                <dd>{formaterJour(e.dateTheorique)} (reportée au jour ouvré)</dd>
              </>
            )}
            <dt>Certitude</dt>
            <dd>{LIBELLES_CERTITUDE[e.certitude]}</dd>
            <dt>Montant prévu</dt>
            <dd>
              {e.montantPrevuCts !== null
                ? formaterMontant(e.montantPrevuCts, e.devise)
                : 'Non renseigné'}
            </dd>
            {e.montantReelCts !== null && (
              <>
                <dt>Montant réel</dt>
                <dd>{formaterMontant(e.montantReelCts, e.devise)}</dd>
              </>
            )}
            {e.regle && (
              <>
                <dt>Règle</dt>
                <dd>
                  {e.regle}
                  {e.note && <div className="discret">{e.note}</div>}
                </dd>
              </>
            )}
            {e.preuve && (
              <>
                <dt>Preuve</dt>
                <dd>
                  <Link to="/pieces/$id" params={{ id: e.preuve.id }}>
                    {e.preuve.nomFichier ?? e.preuve.numero ?? e.preuve.type}
                  </Link>
                </dd>
              </>
            )}
            {e.preuveReleve && (
              <>
                <dt>Constaté sur le relevé</dt>
                <dd>
                  {formaterJour(e.preuveReleve.dateOperation)} ·{' '}
                  {formaterMontant(e.preuveReleve.montantCts, e.preuveReleve.devise)} ·{' '}
                  {e.preuveReleve.libelle}
                  <div className="discret">{e.preuveReleve.compte}</div>
                </dd>
              </>
            )}
          </dl>
        </Ligne>

        {ecriture && (
          <div className="boutons actions-echeance">
            {ouverte && !marquer && (
              <button type="button" className="bouton" onClick={() => setMarquer(true)}>
                Marquer fait
              </button>
            )}
            {ouverte && (
              <button
                type="button"
                className="bouton-discret"
                onClick={async () =>
                  (await confirmer(
                    'Sans objet : les tâches et le paiement ouverts seront annulés.',
                  )) && agir(() => lire(api.echeances[':id']['sans-objet'].$post(param)))
                }
              >
                Sans objet
              </button>
            )}
            {!ouverte && (
              <button
                type="button"
                className="bouton-discret"
                onClick={() => agir(() => lire(api.echeances[':id'].defaire.$post(param)))}
              >
                Défaire
              </button>
            )}
            {e.dateForcee && ouverte && (
              <button
                type="button"
                className="bouton-discret"
                onClick={() =>
                  agir(() =>
                    lire(api.echeances[':id'].$patch({ ...param, json: { dateForcee: false } })),
                  )
                }
              >
                Rendre la date à la règle
              </button>
            )}
          </div>
        )}
        {marquer && (
          <div className="section-ajout">
            <p className="discret">
              Une simple coche suffit. La preuve (accusé de dépôt, avis, facture) et le montant réel
              sont facultatifs. Les tâches restantes seront annulées.
            </p>
            <Formulaire
              champs={[
                {
                  nom: 'preuvePieceId',
                  libelle: 'Preuve',
                  type: 'choix',
                  aide: 'Une pièce de l’échéance, ou reçue récemment pour cette entité.',
                  options: [
                    ...new Map(
                      [...(piecesEcheance.data ?? []), ...(piecesEntite.data ?? [])].map((pc) => [
                        pc.id,
                        {
                          valeur: pc.id,
                          libelle: [
                            pc.emetteur ?? pc.nomFichier,
                            pc.numero,
                            pc.dateDocument && formaterJour(pc.dateDocument),
                          ]
                            .filter(Boolean)
                            .join(' · '),
                        },
                      ]),
                    ).values(),
                  ],
                },
                { nom: 'montantReelCts', libelle: 'Montant réel (€)', type: 'montant' },
              ]}
              initial={{ preuvePieceId: e.preuve?.id ?? piecesEcheance.data?.[0]?.id ?? null }}
              libelleBouton="Confirmer : c’est fait"
              onAnnuler={() => setMarquer(false)}
              onValider={async (v) => {
                await lire(
                  api.echeances[':id']['marquer-fait'].$post({
                    ...param,
                    json: corps(sch.marquerFait, v),
                  }),
                )
                await rafraichir()
                setMarquer(false)
              }}
            />
          </div>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
      </Section>

      <Section
        titre="Tâches"
        libelleAjout="Ajouter une tâche"
        ajout={
          ecriture && ouverte
            ? (fermer) => <FormulaireTache fermer={fermer} echeanceId={id} />
            : undefined
        }
      >
        {lesTaches.data?.length === 0 && (
          <Vide>
            Aucune tâche.
            {e.regle && ' Celles de la règle sont créées à l’approche de leur date.'}
          </Vide>
        )}
        {lesTaches.data?.map((t) => (
          <LigneTache key={t.id} t={t} sansEntite />
        ))}
      </Section>

      <Section titre="Paiement">
        {lesPaiements.data?.length === 0 && (
          <Vide>
            Aucun paiement attendu.
            {e.regle &&
              ' Il apparaît quand un montant prévu est saisi, si la règle prévoit un paiement.'}
          </Vide>
        )}
        {lesPaiements.data?.map((p) => (
          <LignePaiement
            key={p.id}
            p={p}
            onPayer={
              ecriture && ['prevu', 'programme', 'partiel'].includes(p.statut)
                ? () =>
                    agir(() =>
                      lire(api.paiements['marquer-payes'].$post({ json: { paiementIds: [p.id] } })),
                    )
                : undefined
            }
          />
        ))}
      </Section>

      <Section titre="Pièces">
        {piecesEcheance.data?.length === 0 && (
          <Vide>Aucune pièce : un avis ou un accusé reçu sur admin@ s’y rattache seul.</Vide>
        )}
        {piecesEcheance.data?.map((pc) => (
          <LignePiece key={pc.id} p={pc} />
        ))}
      </Section>

      <Section titre="Historique">
        <Historique objetId={id} />
      </Section>
    </main>
  )
}
